import { Table } from '@/components';
import { TablePaginationComp, type PaginationConfig } from '@/components/table/TablePagination';
import type { ColumnConfig } from '@/components/table/Table';
import { IconButton, Tooltip } from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import type { ExportJobListItem } from '../models/ExportJob';
import { EXPORT_KIND_LABELS } from '../models/ExportJob';
import ExportJobStatusChip from './ExportJobStatusChip';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs} s`;
  return `${Math.floor(secs / 60)} min ${secs % 60} s`;
}

const columns: ColumnConfig[] = [
  {
    label: 'Fecha',
    field: 'createdAt',
    render: (value: string) => formatDate(value),
  },
  {
    label: 'Tipo',
    field: 'exportKind',
    render: (value) => EXPORT_KIND_LABELS[value as keyof typeof EXPORT_KIND_LABELS] ?? value,
  },
  {
    label: 'Archivo',
    field: 'fileName',
  },
  {
    label: 'Estado',
    field: 'status',
    render: (value) => <ExportJobStatusChip status={value} />,
  },
  {
    label: 'Duración',
    field: 'durationMs',
    render: (value) => formatDuration(value),
  },
];

interface ExportJobsTableProps {
  jobs: ExportJobListItem[];
  isLoading: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onViewDetail: (jobId: string) => void;
}

const ExportJobsTable = ({
  jobs,
  isLoading,
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onViewDetail,
}: ExportJobsTableProps) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const pagination: PaginationConfig = {
    totalItems,
    totalPages,
    currentPage,
    rowsPerPage: pageSize,
    onPageChange,
    onRowsPerPageChange: () => {},
    rowsPerPageOptions: [20],
  };

  const renderActions = (rowData: ExportJobListItem) => (
    <Tooltip title='Ver detalle'>
      <IconButton size='small' onClick={() => onViewDetail(rowData.jobId)}>
        <ViewIcon fontSize='small' />
      </IconButton>
    </Tooltip>
  );

  return (
    <div className='bg-white rounded-lg shadow-md overflow-x-auto'>
      <Table
        columns={columns}
        data={jobs}
        isLoading={isLoading}
        skeletonRows={pageSize}
        renderActions={renderActions}
        getRowKey={(row) => row.jobId}
      />
      <TablePaginationComp {...pagination} />
    </div>
  );
};

export default ExportJobsTable;
